"use client"

import React from "react"
import ReactMarkdown from "react-markdown"
import type { Components } from "react-markdown"
import remarkGfm from "remark-gfm"
import { useLocale } from "next-intl"
import Link from "next/link"
import { Code2 } from "lucide-react"
import { cn } from "../../lib/utils.js"

interface MarkdownRendererProps {
  content: string
  className?: string
  basePath?: string // Base path for converting relative links
  onDocLinkClick?: (docRouteKey: string) => void // Callback for internal doc links
}

// Helper function to extract text content from React children
function extractText(children: React.ReactNode): string {
  if (typeof children === "string") {
    return children
  }
  if (typeof children === "number") {
    return String(children)
  }
  if (Array.isArray(children)) {
    return children.map(extractText).join("")
  }
  if (React.isValidElement(children)) {
    return extractText((children.props as { children?: React.ReactNode }).children)
  }
  return ""
}

// Helper function to generate anchor ID from header text
function generateAnchorId(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
}

export function MarkdownRenderer({ content, className, basePath = "", onDocLinkClick }: MarkdownRendererProps) {
  const locale = useLocale()

  // Process markdown content to convert internal documentation links to app routes
  const processedContent = processMarkdownLinks(content, basePath, locale)

  return (
    <div className={cn("markdown-content prose prose-slate dark:prose-invert prose-headings:font-semibold prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-code:text-sm prose-pre:bg-muted prose-pre:border prose-pre:border-border max-w-none prose-p:leading-7 prose-ul:my-4 prose-ol:my-4 prose-li:my-2", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Custom heading handlers to add anchor IDs
          h1: ({ children, ...props }: React.ComponentPropsWithoutRef<"h1">) => {
            const text = extractText(children)
            const id = generateAnchorId(text)
            return <h1 id={id} className="mt-8 mb-4 text-3xl font-semibold first:mt-0" {...props}>{children}</h1>
          },
          h2: ({ children, ...props }: React.ComponentPropsWithoutRef<"h2">) => {
            const text = extractText(children)
            const id = generateAnchorId(text)
            return <h2 id={id} className="mt-6 mb-3 text-2xl font-semibold first:mt-0" {...props}>{children}</h2>
          },
          h3: ({ children, ...props }: React.ComponentPropsWithoutRef<"h3">) => {
            const text = extractText(children)
            const id = generateAnchorId(text)
            return <h3 id={id} className="mt-5 mb-2 text-xl font-semibold first:mt-0" {...props}>{children}</h3>
          },
          h4: ({ children, ...props }: React.ComponentPropsWithoutRef<"h4">) => {
            const text = extractText(children)
            const id = generateAnchorId(text)
            return <h4 id={id} className="mt-4 mb-2 text-lg font-semibold first:mt-0" {...props}>{children}</h4>
          },
          h5: ({ children, ...props }: React.ComponentPropsWithoutRef<"h5">) => {
            const text = extractText(children)
            const id = generateAnchorId(text)
            return <h5 id={id} className="mt-4 mb-2 text-base font-semibold first:mt-0" {...props}>{children}</h5>
          },
          h6: ({ children, ...props }: React.ComponentPropsWithoutRef<"h6">) => {
            const text = extractText(children)
            const id = generateAnchorId(text)
            return <h6 id={id} className="mt-3 mb-2 text-sm font-semibold first:mt-0" {...props}>{children}</h6>
          },
          // Style paragraphs
          p: ({ children, ...props }: React.ComponentPropsWithoutRef<"p">) => (
            <p className="my-4 leading-7" {...props}>{children}</p>
          ),
          // Style unordered lists
          ul: ({ children, ...props }: React.ComponentPropsWithoutRef<"ul">) => (
            <ul className="my-4 ml-6 list-disc space-y-2" {...props}>{children}</ul>
          ),
          // Style ordered lists
          ol: ({ children, ...props }: React.ComponentPropsWithoutRef<"ol">) => (
            <ol className="my-4 ml-6 list-decimal space-y-2" {...props}>{children}</ol>
          ),
          // Style list items
          li: ({ children, ...props }: React.ComponentPropsWithoutRef<"li">) => (
            <li className="my-1 pl-1" {...props}>{children}</li>
          ),
          // Custom link handler to intercept documentation links
          a: ({ node, href, children, ...props }: React.ComponentPropsWithoutRef<"a"> & { node?: unknown; href?: string | null }) => {
            // Check if it's an internal documentation link (already processed by processMarkdownLinks)
            // Links that start with basePath should update query params instead of navigating
            if (href && href.startsWith(basePath) && onDocLinkClick) {
              // Parse the URL to extract query parameters properly
              try {
                const url = new URL(href, window.location.origin)
                const docRouteKey = url.searchParams.get("doc")
                const hash = url.hash || ""
                
                if (docRouteKey) {
                  return (
                    <a
                      href={`${basePath}?doc=${docRouteKey}${hash}`}
                      onClick={(e) => {
                        e.preventDefault()
                        onDocLinkClick(docRouteKey)
                      }}
                      className="text-primary hover:underline"
                    >
                      {children}
                    </a>
                  )
                }
              } catch (e) {
                // Fallback: try to extract from path if URL parsing fails
                const pathAfterBase = href.replace(basePath, "").replace(/^\//, "")
                const docRouteKey = pathAfterBase.split("?")[0]?.split("#")[0] || pathAfterBase
                
                if (docRouteKey && docRouteKey !== "" && !docRouteKey.startsWith("#") && !docRouteKey.startsWith("?")) {
                  return (
                    <a
                      href={`${basePath}?doc=${docRouteKey}`}
                      onClick={(e) => {
                        e.preventDefault()
                        onDocLinkClick(docRouteKey)
                      }}
                      className="text-primary hover:underline"
                    >
                      {children}
                    </a>
                  )
                }
              }
            }
            // Other internal app links (already in correct format)
            if (href && href.startsWith(`/${locale}`) && !href.startsWith(basePath)) {
              return (
                <Link href={href} className="text-primary hover:underline">
                  {children}
                </Link>
              )
            }
            // Check if it's a raw markdown file link that wasn't processed
            if (href && (href.startsWith("./") || href.match(/^[A-Z_]+\.md/))) {
              // Convert to app route
              const appRoute = convertDocLinkToRoute(href, basePath, locale)
              if (appRoute) {
                return (
                  <Link href={appRoute} className="text-primary hover:underline">
                    {children}
                  </Link>
                )
              }
            }
            // Mailto links
            if (href && href.startsWith("mailto:")) {
              return (
                <a href={href} className="text-primary hover:underline">
                  {children}
                </a>
              )
            }
            // External links
            if (href && (href.startsWith("http") || href.startsWith("//"))) {
              return (
                <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  {children}
                </a>
              )
            }
            // Internal app links (already in correct format)
            if (href && href.startsWith("/")) {
              return (
                <Link href={href} className="text-primary hover:underline">
                  {children}
                </Link>
              )
            }
            // Default
            return (
              <a href={href} {...props} className="text-primary hover:underline">
                {children}
              </a>
            )
          },
          // Style code blocks - pre element wraps code blocks
          pre: ({ children, ...props }: React.ComponentPropsWithoutRef<"pre">) => {
            // Extract language from code element if it exists
            let language = ""
            React.Children.forEach(children, (child) => {
              if (React.isValidElement(child) && child.props && typeof child.props === 'object' && 'className' in child.props) {
                const className = child.props.className
                if (typeof className === 'string') {
                  const match = /language-(\w+)/.exec(className)
                  if (match) language = match[1]
                }
              }
            })
            
            return (
              <div className="relative my-4">
                {language && (
                  <div className="absolute top-2 right-2 z-10 flex items-center gap-1 text-xs text-muted-foreground bg-muted/80 px-2 py-1 rounded">
                    <Code2 className="h-3 w-3" />
                    <span className="uppercase">{language}</span>
                  </div>
                )}
                <pre
                  className={cn(
                    "block w-full overflow-x-auto rounded-lg bg-muted p-4 text-sm font-mono border border-border",
                    props.className
                  )}
                  {...props}
                >
                  {children}
                </pre>
              </div>
            )
          },
          // Style code blocks - inline code vs block code
          code: ({ node, className, children, ...props }: React.ComponentPropsWithoutRef<"code"> & { node?: unknown; className?: string }) => {
            // If className exists and has language- prefix, it's a block code (inside pre)
            // Otherwise it's inline code
            const isBlockCode = className && className.includes("language-")
            
            if (isBlockCode) {
              // Block code - pre component handles the container styling
              return (
                <code className={cn("text-sm font-mono", className)} {...props}>
                  {children}
                </code>
              )
            }
            
            // Inline code
            return (
              <code className={cn("rounded bg-muted px-1.5 py-0.5 text-sm font-mono", className)} {...props}>
                {children}
              </code>
            )
          },
          // Style blockquotes
          blockquote: ({ children }: React.ComponentPropsWithoutRef<"blockquote">) => (
            <blockquote className="my-4 border-l-4 border-primary/50 pl-4 italic text-muted-foreground">
              {children}
            </blockquote>
          ),
          // Style strong/bold text
          strong: ({ children, ...props }: React.ComponentPropsWithoutRef<"strong">) => (
            <strong className="font-semibold" {...props}>{children}</strong>
          ),
          // Style emphasis/italic text
          em: ({ children, ...props }: React.ComponentPropsWithoutRef<"em">) => (
            <em className="italic" {...props}>{children}</em>
          ),
          // Style horizontal rules
          hr: ({ ...props }: React.ComponentPropsWithoutRef<"hr">) => (
            <hr className="my-8 border-border" {...props} />
          ),
          // Style tables
          table: ({ children }: React.ComponentPropsWithoutRef<"table">) => (
            <div className="my-4 overflow-x-auto">
              <table className="min-w-full divide-y divide-border border border-border rounded-lg">{children}</table>
            </div>
          ),
          th: ({ children }: React.ComponentPropsWithoutRef<"th">) => (
            <th className="bg-muted px-4 py-2 text-left text-sm font-semibold border-b border-border">{children}</th>
          ),
          td: ({ children }: React.ComponentPropsWithoutRef<"td">) => (
            <td className="border-t border-border px-4 py-2 text-sm">{children}</td>
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  )
}

/**
 * Process markdown content to convert documentation file links to app routes
 */
function processMarkdownLinks(content: string, basePath: string, locale: string): string {
  // Convert markdown file links to app routes
  // Patterns to match:
  // - [text](./FILE.md) -> [text](/locale/admin/help/FILE or /locale/affiliate/help/FILE)
  // - [text](FILE.md) -> same as above
  // - [text](./ADMIN_GUIDE.md#section) -> [text](/locale/admin/help/admin-guide#section)
  
  const docFileMap: Record<string, string> = {
    "DOCUMENTATION_INDEX": "index",
    "ADMIN_GUIDE": "admin-guide",
    "ADMIN_ONBOARDING": "admin-onboarding",
    "AFFILIATE_GUIDE": "affiliate-guide",
    "CONCEPTS": "concepts",
    "QUICK_START_ADMIN": "quick-start-admin",
    "QUICK_START_AFFILIATE": "quick-start-affiliate",
    "INTEGRATION_GUIDE": "integration-guide",
    "WEBHOOKS_README": "webhooks",
  }

  let processed = content

  // Determine if this is admin or affiliate documentation based on basePath
  const isAdmin = basePath.includes("/admin/help")
  const helpBase = isAdmin ? `/${locale}/admin/help` : `/${locale}/affiliate/help`

  // Replace markdown file links - convert to query param format
  Object.entries(docFileMap).forEach(([fileKey, routeKey]) => {
    // Pattern: [text](./FILE_KEY.md) or [text](FILE_KEY.md) or [text](./FILE_KEY.md#section)
    // Match: [text](link) where link contains FILE_KEY.md
    const pattern = new RegExp(`\\[([^\\]]+)\\]\\(\.?/?${fileKey}\\.md(?:#([^)]+))?\\)`, "g")
    
    processed = processed.replace(pattern, (_match, linkText, hash) => {
      // Convert to query param format: /help?doc=routeKey
      // If it's the index doc (first one), we can omit the doc param
      const hashPart = hash ? `#${hash.toLowerCase().replace(/\s+/g, "-")}` : ""
      return `[${linkText}](${helpBase}?doc=${routeKey}${hashPart})`
    })
  })

  // Anchor IDs are now handled by custom heading components in ReactMarkdown
  // No need to inject raw HTML anchor tags

  return processed
}

/**
 * Convert documentation file link to app route
 */
function convertDocLinkToRoute(href: string, basePath: string, locale: string): string | null {
  const isAdmin = basePath.includes("/admin/help")
  const helpBase = isAdmin ? `/${locale}/admin/help` : `/${locale}/affiliate/help`

  // Extract filename and hash
  const match = href.match(/([A-Z_]+)\.md(?:#(.+))?/)
  if (!match) return null

  const fileKey = match[1]
  const hash = match[2]

  const routeMap: Record<string, string> = {
    DOCUMENTATION_INDEX: "index",
    ADMIN_GUIDE: "admin-guide",
    ADMIN_ONBOARDING: "admin-onboarding",
    AFFILIATE_GUIDE: "affiliate-guide",
    CONCEPTS: "concepts",
    QUICK_START_ADMIN: "quick-start-admin",
    QUICK_START_AFFILIATE: "quick-start-affiliate",
    INTEGRATION_GUIDE: "integration-guide",
    WEBHOOKS_README: "webhooks",
  }

  const routeKey = routeMap[fileKey]
  if (!routeKey) return null

  const hashPart = hash ? `#${hash.toLowerCase().replace(/\s+/g, "-")}` : ""
  return `${helpBase}/${routeKey}${hashPart}`
}

